/**
 * Körjournalinställningar för toppnod (organisationsnivå).
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import type { DrivingLogSettings } from '@/types/organizations';
import { DEFAULT_DRIVING_LOG_ORG_SETTINGS, mergeDrivingLogOrgSettings } from '@/utils/organizationDrivingLogDefaults';

interface Props {
  value: DrivingLogSettings | undefined | null;
  onChange: (next: DrivingLogSettings) => void;
}

const OrganizationDrivingLogSettings: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const s = mergeDrivingLogOrgSettings(value);

  const patch = (p: Partial<DrivingLogSettings>) => {
    onChange({ ...DEFAULT_DRIVING_LOG_ORG_SETTINGS, ...s, ...p });
  };

  return (
    <div className="space-y-6 p-4 rounded-lg border border-border bg-bgElevated">
      <div>
        <h3 className="text-base font-semibold text-textDefault mb-1">{t('organizations.drivingLog.title')}</h3>
        <p className="text-sm text-textSecondary">{t('organizations.drivingLog.intro')}</p>
      </div>

      <div>
        <h4 className="text-sm font-medium text-textDefault mb-2">{t('organizations.drivingLog.serviceHoursTitle')}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.drivingLog.serviceStart')}</label>
            <input
              type="time"
              value={s.defaultServiceStart?.substring(0, 5) ?? ''}
              onChange={(e) => patch({ defaultServiceStart: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.drivingLog.serviceEnd')}</label>
            <input
              type="time"
              value={s.defaultServiceEnd?.substring(0, 5) ?? ''}
              onChange={(e) => patch({ defaultServiceEnd: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
            />
          </div>
        </div>
        <p className="mt-2 text-xs text-textTertiary">{t('organizations.drivingLog.serviceHoursHelp')}</p>
      </div>

      <label className="flex items-start gap-3 p-4 bg-bgContainer rounded-xl border border-border cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(s.cutOffEnabled)}
          onChange={(e) => patch({ cutOffEnabled: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-border text-bgPrimary"
        />
        <span>
          <span className="font-medium text-textDefault block">{t('organizations.drivingLog.cutOffEnabled')}</span>
          <span className="text-sm text-textSecondary block mt-0.5">{t('organizations.drivingLog.cutOffEnabledHelp')}</span>
        </span>
      </label>

      <div className={`space-y-4 ${s.cutOffEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.drivingLog.cutOffDay')}</label>
          <input
            type="number"
            min={1}
            max={28}
            value={s.cutOffDayOfMonth ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              patch({ cutOffDayOfMonth: v === '' ? undefined : parseInt(v, 10) });
            }}
            className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
          />
          <p className="mt-1 text-xs text-textTertiary">{t('organizations.drivingLog.cutOffDayHelp')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.drivingLog.reminderDays')}</label>
          <input
            type="number"
            min={1}
            max={14}
            value={s.reminderDaysBefore ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              patch({ reminderDaysBefore: v === '' ? undefined : parseInt(v, 10) });
            }}
            className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
          />
          <p className="mt-1 text-xs text-textTertiary">{t('organizations.drivingLog.reminderHelp')}</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.drivingLog.forwardingEmail')}</label>
        <input
          type="text"
          inputMode="email"
          autoComplete="off"
          value={s.forwardingEmail ?? ''}
          onChange={(e) => patch({ forwardingEmail: e.target.value })}
          placeholder={t('organizations.drivingLog.forwardingEmailPlaceholder')}
          className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
        />
        <p className="mt-1 text-xs text-textTertiary">{t('organizations.drivingLog.forwardingEmailHelp')}</p>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="lock-after-submit"
          type="checkbox"
          checked={s.lockAfterSubmit !== false}
          onChange={(e) => patch({ lockAfterSubmit: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-border text-bgPrimary"
        />
        <label htmlFor="lock-after-submit" className="text-sm text-textDefault cursor-pointer">
          <span className="font-medium">{t('organizations.drivingLog.lockAfterSubmit')}</span>
          <p className="text-textSecondary mt-0.5">{t('organizations.drivingLog.lockAfterSubmitHelp')}</p>
        </label>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="odometer-adjustment-lock"
          type="checkbox"
          checked={Boolean(s.odometerAdjustmentLockEnabled)}
          onChange={(e) => patch({ odometerAdjustmentLockEnabled: e.target.checked })}
          className="mt-1 h-4 w-4 rounded border-border text-bgPrimary"
        />
        <label htmlFor="odometer-adjustment-lock" className="text-sm text-textDefault cursor-pointer">
          <span className="font-medium">{t('organizations.drivingLog.odometerAdjustmentLock')}</span>
          <p className="text-textSecondary mt-0.5">{t('organizations.drivingLog.odometerAdjustmentLockHelp')}</p>
        </label>
      </div>
    </div>
  );
};

export default OrganizationDrivingLogSettings;
